import _ from 'lodash';
import { DagDetailParams } from 'store/airflow/enhancedAirflowApi';
import { GetApiProjectResourceApiResponse, ProjectResourceAssignmentModel } from 'store/userOrg/userOrgApi';
import { GetApiLocationApiResponse } from 'store/userOrg/userOrgAdministrationApi';
import { Dag } from 'store/airflow/airflowApi';

export enum WorkflowType {
    JOBSCRIPT = 'jobscript',
    CONTAINER = 'container',
    HPC_COMMAND = 'hpc command',
    CUSTOM_COMPLEX = 'complex',
    BULK_COPY = 'bulk_copy',
}

export enum AirflowTaskName {
    SELECT_OPERATOR = 'select_operator',
    HEAPPE_SUBMIT_JOB = 'heappe_submit_job',
    HEAPPE_PREPARE_JOB = 'heappe_prepare_job',
}

export enum AirflowState {
    SUCCESS = 'success',
    RUNNING = 'running',
    FAILED = 'failed',
    UPSTREAM_FAILED = 'upstream_failed',
    SKIPPED = 'skipped',
    QUEUED = 'queued',
    SCHEDULED = 'scheduled',
    NO_STATUS = 'no_status',
    RESTARTING = 'restarting',
    REMOVED = 'removed',
    SHUTDOWN = 'shutdown',
    DEFERRED = 'deferred',
    UP_FOR_RESCHEDULE = 'up_for_reschedule',
    UP_FOR_RETRY = 'up_for_retry',
}

export enum WorkflowTagCategory {
    PROJECT = 'project',
    TYPE = 'type',
}

const findWorkflowTagValue = (dagDetailsData: Dag | undefined, category: WorkflowTagCategory) => {
    const prefix = `${category}__`;
    const foundTag = dagDetailsData?.tags?.find(tag => tag?.name?.startsWith(prefix));
    return foundTag ? foundTag?.name?.replace(prefix, '') : undefined;
};

const transformParams = (params: DagDetailParams) => {
    const newParams = {};

    params &&
        Object.entries(params).forEach(([key, value]) => {
            newParams[key] = value.value ?? value;
        });

    return newParams;
};

const availableHPCSystemZonesData = (
    resourcesData: GetApiProjectResourceApiResponse | undefined,
    locationsList: GetApiLocationApiResponse | undefined
) => {
    const filteredResults: { assignment: ProjectResourceAssignmentModel; name: string | null | undefined }[] = [];

    resourcesData?.forEach(resource => {
        resource.Assignments?.forEach(assignment => {
            const matchingLocation = locationsList?.find(location => {
                return (
                    location.Id === assignment.LocationId &&
                    location.LocationTypeId === 1 &&
                    assignment.Specifications?.find(spec => spec.Key?.includes('HPC_CUSTOM_SCRIPT_'))
                );
            });

            if (matchingLocation) {
                filteredResults.push({ assignment, name: matchingLocation?.Name });
            }
        });
    });

    const zonesData = filteredResults
        ?.map(item => {
            const zone = item?.assignment?.Specifications?.find(spec => spec.Key === 'ZONE_NAME')?.Value;
            const spec = JSON.parse(
                item?.assignment?.Specifications?.find(spec => spec.Key?.includes('HPC_CUSTOM_SCRIPT_'))
                    ?.Value as string
            );
            const resourceName = resourcesData?.find(
                resource => resource.Id === item?.assignment?.ProjectResourceId
            )?.Name;

            return {
                value: zone,
                label: `${resourceName} - ${item?.name}`,
                assignment: item?.assignment,
                spec,
            };
        })
        ?.filter(item => item.value);

    const uniqueZonesData = _.uniqBy(zonesData, 'label');
    return uniqueZonesData;
};

const extractPath = path => {
    const keywords = ['project', 'public', 'user'];
    for (let keyword of keywords) {
        if (path?.includes(keyword)) {
            return `${keyword}${path.split(keyword)[1]}`;
        }
    }
    return null;
};

const removePrefixes = path => {
    const extractedPath = extractPath(path);
    return extractedPath;
};

const trimDagIdPrefix = (dagId: string | null | undefined, shortNames: string[]): string => {
    if (!dagId) return '';
    for (const shortName of shortNames) {
        const prefix = `${shortName}_`;
        if (dagId.startsWith(prefix)) return dagId.slice(prefix.length);
    }
    return dagId;
};

const typeChipColor = (type: WorkflowType | string | undefined): 'primary' | 'success' | 'warning' | 'info' => {
    if (type === WorkflowType.JOBSCRIPT) return 'primary';
    if (type === WorkflowType.CONTAINER) return 'success';
    if (type === WorkflowType.HPC_COMMAND) return 'warning';
    return 'info';
};

export {
    findWorkflowTagValue,
    trimDagIdPrefix,
    transformParams,
    availableHPCSystemZonesData,
    removePrefixes,
    typeChipColor,
};
